import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Eye, EyeOff, ExternalLink, ChevronRight } from "lucide-react";
import MenuServiceImport from "@/pages/MenuServiceImport";
import VendorVerification from "@/pages/VendorVerification";

const CATS = ["catering","restaurant","venue","dj","florist","rentals","av","security"];

export default function AdminOnboarding() {
  const [vendors, setVendors] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [openNew, setOpenNew] = useState(false);
  const [activeVendor, setActiveVendor] = useState(null);
  const [form, setForm] = useState({ name: "", category: "catering", city: "", description: "", price_from: 0, cover_image: "" });

  const load = () => api.get("/admin/vendors").then(({ data }) => setVendors(data));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return vendors.filter(v => {
      const matchQ = !q || v.name?.toLowerCase().includes(q.toLowerCase()) || v.city?.toLowerCase().includes(q.toLowerCase());
      const isPub = v.published !== false;
      const matchFilter = filter === "all" || (filter === "published" && isPub) || (filter === "unpublished" && !isPub);
      return matchQ && matchFilter;
    });
  }, [vendors, q, filter]);

  const createVendor = async () => {
    if (!form.name || !form.city) return toast.error("Name and city required");
    try {
      const { data } = await api.post("/vendors", { ...form, tagline: form.description.slice(0, 100), price_unit: "per event", categories: [form.category] });
      // start as unpublished so admin can prep it
      await api.put(`/admin/vendors/${data.vendor_id}/publish`, { published: false });
      toast.success("Vendor created (unpublished)");
      setOpenNew(false); setForm({ name: "", category: "catering", city: "", description: "", price_from: 0, cover_image: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const togglePublish = async (v) => {
    const next = !(v.published !== false);
    await api.put(`/admin/vendors/${v.vendor_id}/publish`, { published: next });
    toast.success(next ? "Published to marketplace" : "Unpublished");
    load();
    if (activeVendor?.vendor_id === v.vendor_id) setActiveVendor({ ...activeVendor, published: next });
  };

  if (activeVendor) return (
    <div data-testid="admin-onboarding-detail">
      <button data-testid="onboarding-back" onClick={() => setActiveVendor(null)} className="text-sm link-underline text-[color:var(--text-muted)] mb-6">← Back to onboarding hub</button>
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="eyebrow mb-2">Onboarding · {activeVendor.category}</div>
          <h2 className="text-3xl font-semibold">{activeVendor.name}</h2>
          <div className="text-sm text-[color:var(--text-muted)] mt-1">{activeVendor.city}</div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to={`/vendor/${activeVendor.vendor_id}`} target="_blank">
            <Button variant="outline" className="rounded-lg h-10"><ExternalLink className="w-4 h-4 mr-2" />Preview</Button>
          </Link>
          <Button data-testid="publish-toggle" onClick={() => togglePublish(activeVendor)} className={activeVendor.published !== false ? "rounded-lg h-10 bg-[color:var(--text)] text-white hover:bg-[color:var(--text)]/90" : "btn-sage rounded-lg h-10"}>
            {activeVendor.published !== false ? (<><EyeOff className="w-4 h-4 mr-2" />Unpublish</>) : (<><Eye className="w-4 h-4 mr-2" />Publish</>)}
          </Button>
        </div>
      </div>
      <Tabs defaultValue="menus">
        <TabsList className="bg-[color:var(--surface)]">
          <TabsTrigger value="menus" data-testid="onb-tab-menus">Menus &amp; Imports</TabsTrigger>
          <TabsTrigger value="verification" data-testid="onb-tab-verification">Verification</TabsTrigger>
        </TabsList>
        <TabsContent value="menus" className="mt-6">
          <MenuServiceImport vendors={[activeVendor]} />
        </TabsContent>
        <TabsContent value="verification" className="mt-6">
          <VendorVerification vendors={[activeVendor]} />
        </TabsContent>
      </Tabs>
    </div>
  );

  return (
    <div data-testid="admin-onboarding">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="eyebrow mb-2">Pre-launch curation</div>
          <h2 className="text-2xl font-semibold">Seed the marketplace</h2>
          <p className="text-sm text-[color:var(--text-muted)] max-w-2xl mt-2">
            Add vendors, upload their menus, import from their website, approve verification documents, and publish when ready. Unpublished listings are hidden from customers.
          </p>
        </div>
        <Button data-testid="onboarding-add-vendor" onClick={() => setOpenNew(true)} className="btn-sage rounded-lg h-11 px-6">
          <Plus className="w-4 h-4 mr-2" /> Add vendor
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Input data-testid="onboarding-search" placeholder="Search by name or city" value={q} onChange={e => setQ(e.target.value)} className="max-w-xs h-11 bg-white" />
        <div className="flex gap-2">
          {["all","published","unpublished"].map(f => (
            <button key={f} data-testid={`onboarding-filter-${f}`} onClick={() => setFilter(f)}
              className={`chip ${filter === f ? "active" : ""}`}>{f}</button>
          ))}
        </div>
        <div className="ml-auto text-xs text-[color:var(--text-muted)]">
          {vendors.filter(v => v.published !== false).length} published · {vendors.filter(v => v.published === false).length} in prep
        </div>
      </div>

      <div className="space-y-2">
        {filtered.length === 0 ? (
          <div className="text-sm text-[color:var(--text-muted)]" data-testid="onboarding-empty">No vendors match.</div>
        ) : filtered.map(v => {
          const isPub = v.published !== false;
          return (
            <button key={v.vendor_id} data-testid={`onboarding-row-${v.vendor_id}`} onClick={() => setActiveVendor(v)}
              className="w-full text-left rounded-xl border border-[color:var(--border)] bg-white p-4 flex items-center justify-between gap-3 hover:bg-[color:var(--surface)] transition-colors">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm">{v.name}</span>
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${isPub ? "bg-[color:var(--verified-soft)] text-[color:var(--verified)]" : "bg-[#EFEADF] text-[#5C5C5C]"}`}>
                    {isPub ? "Published" : "In prep"}
                  </span>
                  {v.verified && <span className="badge-verified">Verified</span>}
                </div>
                <div className="text-xs text-[color:var(--text-muted)] mt-1">{v.category} · {v.city}</div>
              </div>
              <ChevronRight className="w-4 h-4 text-[color:var(--text-muted)]" />
            </button>
          );
        })}
      </div>

      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent data-testid="new-vendor-dialog" className="max-w-lg bg-[color:var(--bg)]">
          <DialogHeader><DialogTitle className="text-2xl font-semibold">Add a vendor</DialogTitle></DialogHeader>
          <p className="text-xs text-[color:var(--text-muted)]">Create the listing on behalf of a business. It will start as <strong>unpublished</strong> so you can prep menus and verification before it goes live.</p>
          <div className="space-y-3 mt-2">
            <Input data-testid="onb-vendor-name" placeholder="Business name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="h-11 bg-white" />
            <div className="grid grid-cols-2 gap-3">
              <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                <SelectTrigger data-testid="onb-vendor-category" className="bg-white h-11"><SelectValue /></SelectTrigger>
                <SelectContent>{CATS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
              <Input data-testid="onb-vendor-city" placeholder="City" value={form.city} onChange={e => setForm({...form, city: e.target.value})} className="h-11 bg-white" />
            </div>
            <Textarea data-testid="onb-vendor-description" placeholder="Description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3} className="bg-white" />
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="onb-vendor-price" type="number" placeholder="Starting price" value={form.price_from} onChange={e => setForm({...form, price_from: +e.target.value})} className="h-11 bg-white" />
              <Input data-testid="onb-vendor-cover" placeholder="Cover image URL (optional)" value={form.cover_image} onChange={e => setForm({...form, cover_image: e.target.value})} className="h-11 bg-white" />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="onb-vendor-create" onClick={createVendor} className="btn-sage rounded-lg">Create as unpublished</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
